import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { useI18n } from "./I18nContext";

export default function AccountMenu() {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const onLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/login");
  };

  const goChangePassword = () => {
    setOpen(false);
    navigate("/app/settings#change-password");
  };

  const initials = (user?.name || "?").split(" ").map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

  return (
    <>
      <div className="tb-acc-menu-wrap" ref={ref}>
        <button
          type="button"
          className="tb-acc-menu-trigger"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={open}
          data-testid="account-menu-trigger"
        >
          <span className="tb-acc-avatar" aria-hidden="true">{initials}</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.65 }}>
            <circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" />
          </svg>
        </button>
        {open && (
          <div className="tb-acc-menu-pop" role="menu" data-testid="account-menu-pop">
            <div className="tb-acc-menu-header">
              <strong>{user?.name}</strong>
              <div className="tb-muted" style={{ fontSize: "0.78rem", marginTop: 2 }}>{user?.email}</div>
            </div>
            <button type="button" className="tb-acc-menu-item" onClick={() => { setOpen(false); navigate(`/app/u/${user.id}`); }} data-testid="menu-profile">
              <span className="tb-acc-menu-icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
              </span>
              {t("menu.profile")}
            </button>
            <button type="button" className="tb-acc-menu-item" onClick={() => { setOpen(false); navigate("/app/whats-next"); }} data-testid="menu-whats-next">
              <span className="tb-acc-menu-icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                </svg>
              </span>
              {t("menu.whatsNext")}
            </button>
            <button type="button" className="tb-acc-menu-item" onClick={() => { setOpen(false); navigate("/app/surveys"); }} data-testid="menu-surveys">
              <span className="tb-acc-menu-icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="16" rx="2"/><path d="M8 10h8M8 14h5"/>
                </svg>
              </span>
              {t("menu.surveys")}
            </button>
            <button type="button" className="tb-acc-menu-item" onClick={() => { setOpen(false); navigate("/app/feedback"); }} data-testid="menu-feedback">
              <span className="tb-acc-menu-icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                </svg>
              </span>
              {t("menu.sendFeedback")}
            </button>
            {user?.role === "admin" && (
              <button type="button" className="tb-acc-menu-item" onClick={() => { setOpen(false); navigate("/app/admin"); }} data-testid="menu-admin">
                <span className="tb-acc-menu-icon" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2l9 4-9 4-9-4 9-4z"/><path d="M3 10l9 4 9-4"/><path d="M3 14l9 4 9-4"/>
                  </svg>
                </span>
                {t("menu.adminConsole")}
              </button>
            )}
            <button type="button" className="tb-acc-menu-item" onClick={goChangePassword} data-testid="menu-change-password">
              <span className="tb-acc-menu-icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </span>
              {t("menu.changePassword")}
            </button>
            <div className="tb-acc-menu-sep" />
            <div className="tb-acc-menu-section">
              <span className="tb-acc-menu-label">{t("menu.language")}</span>
              <div className="tb-acc-lang">
                <button type="button" className={lang === "en" ? "on" : ""} onClick={() => setLang("en")} data-testid="menu-lang-en">English</button>
                <button type="button" className={lang === "fr" ? "on" : ""} onClick={() => setLang("fr")} data-testid="menu-lang-fr">Français</button>
              </div>
            </div>
            <div className="tb-acc-menu-sep" />
            <button type="button" className="tb-acc-menu-item danger" onClick={onLogout} data-testid="menu-logout">
              <span className="tb-acc-menu-icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
              </span>
              {t("menu.signOut")}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
